/**
 * Theme controller — system-follows-by-default with an optional persisted
 * override.
 *
 *   preference: 'system' | 'light' | 'dark'   (what the user chose)
 *   resolved:   'light' | 'dark'              (what is actually applied)
 *
 * 'system' tracks the OS `prefers-color-scheme` live. Choosing 'light' or
 * 'dark' pins it permanently (localStorage) until the user picks 'system'
 * again. Applying a theme toggles the `.dark` class on <html>, which flips
 * every CSS-variable-backed color token (see src/theme/theme.css).
 *
 * The same logic runs as an inline script in index.html before first paint to
 * avoid a flash of the wrong theme; this module is the runtime source of truth
 * and keeps the live system listener wired.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'forge-theme';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function getPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall through */
  }
  return 'system';
}

export function resolveTheme(pref: ThemePreference = getPreference()): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

/** Apply the resolved theme to <html> and sync the mobile browser chrome color. */
function applyResolved(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#172B4D' : '#FAFBFC');
}

// ── Subscribers (so React can re-render when the theme changes) ───────────────
const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Change the user's preference, persist it, and re-apply. */
export function setPreference(pref: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* ignore persistence failure — still applies for this session */
  }
  applyResolved(resolveTheme(pref));
  emit();
}

let mediaListenerBound = false;

/**
 * Idempotent bootstrap: apply the current preference and (once) wire the live
 * system listener so 'system' preference tracks OS changes in real time.
 */
export function initTheme(): void {
  applyResolved(resolveTheme());

  if (mediaListenerBound || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return;
  }
  mediaListenerBound = true;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (getPreference() === 'system') {
      applyResolved(resolveTheme('system'));
      emit();
    }
  };
  // addEventListener is the modern API; older Safari only has addListener.
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
  else if (typeof mq.addListener === 'function') mq.addListener(onChange);
}
